import {
  sortDirectionEnum,
  type CurrentFiltersType,
  type FilterOption,
  type SortDirectionType,
} from "@/types/common/filters";
import { mergeFilters } from "@/utils/filters";
import React, { useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

/**
 * Custom hook to manage sorting, filtering, and pagination state for a page.
 * TODO: integrate with usePagination when API call is implemented.
 * Based on request-list table page implementation, hence, can be replaced there in the future.
 * @returns
 */
interface UsePageSortFiltersProps {
  defaultFilters?: CurrentFiltersType[];
  defaultFormatFilters?: FilterOption[];
  defaultOrderField?: string;
}

export const usePageSortFilters = (props?: UsePageSortFiltersProps) => {
  const {
    defaultFilters = [],
    defaultFormatFilters = [],
    defaultOrderField = "name",
  } = props || {};
  const [page, setPage] = useState<number>(1);
  const [searchInput, setSearchInput] = useState("");
  const [hasSortApplied, setHasSortApplied] = useState<boolean>(false);
  const [orderField, setOrderField] = useState<string | undefined>(
    defaultOrderField,
  );
  const [filters, setFilters] = useState<CurrentFiltersType[] | []>(
    defaultFilters,
  );
  const [formatFilters, setFormatFilters] =
    useState<FilterOption[]>(defaultFormatFilters);
  const [deferredQuery] = useDebounceValue(searchInput || "", 500);

  const [orderDirection, setOrderDirection] = useState<SortDirectionType>(
    sortDirectionEnum.asc,
  );
  const [selectedEntities, setSelectedEntities] = useState<object>({});

  const handleApplySort = (
    field: string | undefined,
    direction: SortDirectionType,
  ) => {
    setPage(1);
    setOrderField(field ?? "");
    setOrderDirection(direction);

    if (field === "name" && direction === sortDirectionEnum.asc) {
      setHasSortApplied(false);
      return;
    }

    setHasSortApplied(true);
  };

  const handleFilterChange = (
    newFilters: React.SetStateAction<CurrentFiltersType[] | []>,
  ) => {
    setPage(1);
    setFilters(newFilters);
  };

  const handleSearchInputChange = (value: string) => {
    setPage(1);
    setSearchInput(value);
  };

  const query = useMemo(() => {
    const formattedFiltersAsCurrentFilters: CurrentFiltersType[] = formatFilters
      .filter((filter) => filter.isUpdated && filter.result)
      .map((filter) => ({
        [filter.field]: filter.result,
      }));

    // Combine both filters
    const allFilters = [...filters, ...formattedFiltersAsCurrentFilters];

    return {
      filter:
        allFilters.length > 0
          ? {
              ...mergeFilters(allFilters),
              ...(deferredQuery
                ? {
                    search: deferredQuery,
                  }
                : {}),
            }
          : {
              ...(deferredQuery
                ? {
                    search: deferredQuery,
                  }
                : {}),
            },
      order_by: orderField
        ? `${orderDirection === "desc" ? "!" : ""}${orderField}`
        : undefined,
      page,
      per_page: 15,
    };
  }, [filters, formatFilters, deferredQuery, orderDirection, orderField, page]);

  return {
    page,
    setPage,
    searchInput,
    setSearchInput: handleSearchInputChange,
    hasSortApplied,
    orderField,
    orderDirection,
    filters,
    selectedEntities,
    query,
    setSelectedEntities,
    handleApplySort,
    handleFilterChange,
    formatFilters,
    setFormatFilters,
  };
};
